import { useCallback, useEffect, useState } from 'react'
import { Search, RefreshCcw, Download, ArrowDownWideNarrow, ArrowUpNarrowWide } from 'lucide-react'
import * as XLSX from 'xlsx'
import Header from '@/components/layout/Header'
import MemberRow, { type Member, type MemberPosition } from '@/components/members/MemberRow'
import { positionLabel } from '@/lib/myPositions'
import MemberManageDialog from '@/components/members/MemberManageDialog'
import WdlRosterPanel from '@/components/members/WdlRosterPanel'
import Panel, { PanelEmpty, panelDataChip, panelGhostButton, panelIconButton, panelInput } from '@/components/layout/Panel'
import EmojiGlyph from '@/components/ui/EmojiGlyph'
import { useAuth, isAdmin, isOperator } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'

// Sıralama ölçütleri. Her birinin doğal yönü var (ad A→Z, gerisi en çok / en
// yeni önce); yön düğmesi onu çevirir. Değeri bilinmeyen üye (gizli profil,
// bağlanmamış Steam, katılımı kayıtsız) yönden bağımsız hep sonda kalır: "en
// az oynayan" sıralamasının başını Steam'i okunamayanlar doldurmamalı.
const SORT_KEYS = ['name', 'lastSeen', 'playtimeTotal', 'playtimeFortnight', 'clanJoined'] as const
type SortKey = typeof SORT_KEYS[number]
type SortState = { key: SortKey; desc: boolean }

const SORT_STORAGE_KEY = 'tll_members_sort'
const NATURAL_DESC: Record<SortKey, boolean> = {
  name: false, lastSeen: true, playtimeTotal: true, playtimeFortnight: true, clanJoined: true,
}

function loadSort(): SortState {
  try {
    const raw = JSON.parse(localStorage.getItem(SORT_STORAGE_KEY) ?? 'null')
    if (raw && SORT_KEYS.includes(raw.key)) return { key: raw.key, desc: !!raw.desc }
  } catch { /* yok ya da okunamıyor: varsayılana düş */ }
  return { key: 'name', desc: false }
}

export default function Members() {
  const { user } = useAuth()
  const { T, lang } = useLanguage()
  const canEdit = isOperator(user)
  // Roles are granted in Discord now; admins can still see who holds what.
  const canSeeRoles = isAdmin(user)
  // SteamID64s are the staff's working data — the roster itself only names the
  // linked account. The backend leaves the ids out for everyone else, so this
  // only decides whether the line is drawn, never whether the data is there.
  const canSeeSteamIds = isOperator(user)

  const [members, setMembers] = useState<Member[]>([])
  const [lastSeen, setLastSeen] = useState<Record<string, number>>({})
  const [onlineIds, setOnlineIds] = useState<Set<string>>(new Set())
  const [roles, setRoles] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [sort, setSortState] = useState<SortState>(loadSort)
  const setSort = (next: SortState) => {
    setSortState(next)
    try { localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(next)) } catch { /* yalnızca bu oturum */ }
  }
  // Settings'teki oynanan mevkiler: hem kendi seçicin hem süzgeç bunu okur.
  const [positionOptions, setPositionOptions] = useState<MemberPosition[]>([])
  // null = tümü, 'none' = pozisyon seçmemişler.
  const [positionFilter, setPositionFilter] = useState<number | 'none' | null>(null)
  // Seçili pozisyonun içinde tek bir alt sınıf; pozisyon değişince düşer.
  const [specFilter, setSpecFilter] = useState<string | null>(null)
  const choosePosition = (next: number | 'none' | null) => {
    setPositionFilter(next)
    setSpecFilter(null)
  }
  // Satır ayrıntısı aynı anda tek üyede açılır; hepsi açık olsaydı liste yine
  // eski kart yığınına dönerdi.
  const [openId, setOpenId] = useState<string | null>(null)
  const [managingId, setManagingId] = useState<string | null>(null)
  const managing = members.find(m => m.id === managingId) ?? null

  const patchMember = (id: string, patch: Partial<Member>) =>
    setMembers(prev => prev.map(m => (m.id === id ? { ...m, ...patch } : m)))

  // Açık ayrıntı, dışına tıklanınca ya da Esc ile kapanır.
  useEffect(() => {
    if (!openId) return
    const onPointer = (e: PointerEvent) => {
      if (!(e.target as HTMLElement).closest?.(`[data-member-row="${openId}"]`)) setOpenId(null)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenId(null) }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [openId])

  const fetchMembers = useCallback(async (force = false) => {
    setLoading(true)
    setError(null)
    try {
      const url = force ? '/api/members?refresh=true' : '/api/members'
      const reqs: Promise<Response>[] = [
        fetch(url),
        fetch('/api/online/all'),
        fetch('/api/online'),
        fetch('/api/match-classes'),
      ]
      if (canSeeRoles) reqs.push(fetch('/api/permissions', { credentials: 'include' }))

      const [membersRes, lastSeenRes, onlineRes, classesRes, permsRes] = await Promise.all(reqs)

      if (membersRes.ok) {
        setMembers(await membersRes.json())
      } else {
        const data = await membersRes.json().catch(() => ({}))
        setError(data.detail ?? `Error ${membersRes.status}`)
      }
      if (lastSeenRes.ok) setLastSeen(await lastSeenRes.json())
      if (onlineRes.ok) {
        const online: { id: string }[] = await onlineRes.json()
        setOnlineIds(new Set(online.map(u => u.id)))
      }
      if (classesRes.ok) {
        const classes: (MemberPosition & { kind: string })[] = await classesRes.json()
        setPositionOptions(classes.filter(c => c.kind === 'primary').map(({ id, name, emoji, specs }) => ({ id, name, emoji, specs })))
      }
      if (permsRes?.ok) {
        const perms: { discord_id: string; role: string }[] = await permsRes.json()
        const map: Record<string, string> = {}
        perms.forEach(p => { map[p.discord_id] = p.role })
        setRoles(map)
      }
    } catch {
      setError(T.members.serverError)
    } finally {
      setLoading(false)
    }
  }, [canSeeRoles])

  useEffect(() => { fetchMembers(false) }, [fetchMembers])

  // Lig bandında bir başvuru cevaplanınca kartların rozeti de tazelensin.
  const refreshQuiet = useCallback(() => { void fetchMembers(false) }, [fetchMembers])

  const downloadXLSX = () => {
    const playtime = (m: Member) => {
      const p = m.wardogs_playtime
      const hours = (minutes: number) => Math.round(minutes / 6) / 10
      if (!p) return { [T.members.playtimeTotalColumn]: '', [T.members.playtimeFortnightColumn]: '' }
      if ('private' in p) return { [T.members.playtimeTotalColumn]: T.members.playtimePrivate, [T.members.playtimeFortnightColumn]: T.members.playtimePrivate }
      return {
        [T.members.playtimeTotalColumn]: hours(p.total),
        [T.members.playtimeFortnightColumn]: hours(p.two_weeks),
      }
    }
    const positions = (m: Member) => ({
      [T.members.positions.column]: (m.positions ?? []).map(positionLabel).join(', '),
    })
    const rows = members.map(m => (canSeeSteamIds
      ? { 'Nickname': m.display_name, 'Steam account': m.steam_name ?? '', 'SteamID64': m.steam_id ?? '', ...positions(m), ...playtime(m) }
      : { 'Nickname': m.display_name, 'Steam account': m.steam_name ?? '', ...positions(m), ...playtime(m) }
    ))
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Members')
    XLSX.writeFile(wb, 'tll_members.xlsx')
  }

  // null = bu ölçütte değeri yok, sona gider.
  const sortValue = (m: Member): number | string | null => {
    const p = m.wardogs_playtime
    switch (sort.key) {
      case 'name': return m.display_name
      case 'lastSeen': return onlineIds.has(m.id) ? Infinity : (lastSeen[m.id] || null)
      case 'playtimeTotal': return p && !('private' in p) ? p.total : null
      case 'playtimeFortnight': return p && !('private' in p) ? p.two_weeks : null
      case 'clanJoined': return m.clan_joined_at || null
    }
  }
  const collator = new Intl.Collator(lang, { sensitivity: 'base', numeric: true })
  const compare = (a: Member, b: Member) => {
    const va = sortValue(a)
    const vb = sortValue(b)
    if (va === null || vb === null) {
      if (va === vb) return collator.compare(a.display_name, b.display_name)
      return va === null ? 1 : -1
    }
    const d = typeof va === 'string' ? collator.compare(va, vb as string) : (va as number) - (vb as number)
    // Eşitlikte ad karar verir, yönden bağımsız: aynı sürede iki kişi her
    // seferinde aynı sırada dursun.
    if (d === 0) return collator.compare(a.display_name, b.display_name)
    return sort.desc ? -d : d
  }

  const q = search.toLocaleLowerCase(lang)
  const matchesSearch = (m: Member) => [m.display_name, m.username, m.steam_name, m.steam_id]
    .some(v => v?.toLocaleLowerCase(lang).includes(q))
  const matchesPosition = (m: Member) => {
    if (positionFilter === null) return true
    const picked = m.positions ?? []
    if (positionFilter === 'none') return picked.length === 0
    const position = picked.find(p => p.id === positionFilter)
    if (!position) return false
    return specFilter === null || position.specs.some(spec => spec.name === specFilter)
  }
  const visible = members.filter(m => matchesSearch(m) && matchesPosition(m))
  const tll = visible.filter(m => m.team === 'TLL').sort(compare)
  const tlla = visible.filter(m => m.team === 'TLLA').sort(compare)
  const positionCount = (id: number) => members.filter(m => m.positions?.some(p => p.id === id)).length
  const noPositionCount = members.filter(m => !m.positions?.length).length
  const filteredOption = positionOptions.find(o => o.id === positionFilter)
  const specCount = (id: number, name: string) => members.filter(m =>
    m.positions?.some(p => p.id === id && p.specs.some(spec => spec.name === name))
  ).length

  const renderRoster = (list: Member[]) => (
    // Sütun sayısı alana göre: üçüncü sütun, ikinci satırdaki Steam adını
    // okunamayacak kadar sıkıştırmayana kadar açılmaz.
    <div className="grid grid-cols-1 gap-x-5 min-[1024px]:grid-cols-2 min-[1500px]:grid-cols-3 min-[1850px]:grid-cols-4">
      {list.map(m => (
        <MemberRow
          key={m.id}
          member={m}
          lastSeen={lastSeen[m.id] ?? 0}
          isOnline={onlineIds.has(m.id)}
          canSeeRoles={canSeeRoles}
          memberRole={roles[m.id] ?? 'member'}
          canSeeSteamId={canSeeSteamIds}
          open={openId === m.id}
          onToggle={() => setOpenId(openId === m.id ? null : m.id)}
          onManage={canEdit ? () => setManagingId(m.id) : undefined}
        />
      ))}
    </div>
  )

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header title={T.members.title} />
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] max-w-sm flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder={T.members.searchPlaceholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className={`${panelInput} pl-8`}
            />
          </div>
          <div className="flex items-center gap-1.5">
            <label htmlFor="members-sort" className="text-xs text-muted-foreground">
              {T.members.sortLabel}
            </label>
            <select
              id="members-sort"
              value={sort.key}
              onChange={e => {
                const key = e.target.value as SortKey
                setSort({ key, desc: NATURAL_DESC[key] })
              }}
              className={`${panelInput} h-8 w-auto py-0 pr-7`}
            >
              {SORT_KEYS.map(k => (
                <option key={k} value={k}>{T.members.sortBy[k]}</option>
              ))}
            </select>
            <button
              type="button"
              className={panelIconButton}
              onClick={() => setSort({ ...sort, desc: !sort.desc })}
              title={sort.desc ? T.members.sortDesc : T.members.sortAsc}
              aria-label={sort.desc ? T.members.sortDesc : T.members.sortAsc}
            >
              {sort.desc
                ? <ArrowDownWideNarrow className="h-3.5 w-3.5" />
                : <ArrowUpNarrowWide className="h-3.5 w-3.5" />}
            </button>
          </div>
          <p className="ml-auto text-xs text-muted-foreground">
            {loading ? T.members.loading : T.members.count.replace('{n}', String(members.length))}
          </p>
          {/* Yeşil bir düğme panelde başka hiçbir yerde yok; renk Excel'in,
              bizim değil. Dosya biçimini söyleyen tek şey o renkti, artık
              etiketin kendisi söylüyor — düğme de bandın diline döndü. */}
          {canEdit && members.length > 0 && (
            <button className={panelGhostButton} onClick={downloadXLSX} disabled={loading}>
              <Download className="h-3 w-3" />
              {T.members.exportMembers}
            </button>
          )}
          <button className={panelGhostButton} onClick={() => fetchMembers(true)} disabled={loading}>
            <RefreshCcw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            {T.members.refresh}
          </button>
        </div>

        {positionOptions.length > 0 && members.length > 0 && (
          <div role="group" aria-label={T.members.positions.filterLabel} className="flex flex-wrap gap-1.5">
            <button
              type="button"
              aria-pressed={positionFilter === null}
              onClick={() => choosePosition(null)}
              className={panelDataChip(positionFilter === null)}
            >
              {T.members.positions.filterAll}
            </button>
            {positionOptions.map(o => (
              <button
                key={o.id}
                type="button"
                aria-pressed={positionFilter === o.id}
                onClick={() => choosePosition(positionFilter === o.id ? null : o.id)}
                className={panelDataChip(positionFilter === o.id)}
              >
                <EmojiGlyph emoji={o.emoji} size={14} title={o.name} />
                {o.name}
                <span className="tabular-nums text-muted-foreground/60">{positionCount(o.id)}</span>
              </button>
            ))}
            <button
              type="button"
              aria-pressed={positionFilter === 'none'}
              onClick={() => choosePosition(positionFilter === 'none' ? null : 'none')}
              className={panelDataChip(positionFilter === 'none')}
            >
              {T.members.positions.filterNone}
              <span className="tabular-nums text-muted-foreground/60">{noPositionCount}</span>
            </button>
          </div>
        )}

        {filteredOption && filteredOption.specs.length > 0 && (
          <div role="group" aria-label={filteredOption.name} className="flex flex-wrap gap-1.5 border-l border-white/10 pl-3">
            {filteredOption.specs.map(spec => (
              <button
                key={spec.name}
                type="button"
                aria-pressed={specFilter === spec.name}
                onClick={() => setSpecFilter(specFilter === spec.name ? null : spec.name)}
                className={panelDataChip(specFilter === spec.name)}
              >
                <EmojiGlyph emoji={spec.emoji} size={14} title={spec.name} />
                {spec.name}
                <span className="tabular-nums text-muted-foreground/60">{specCount(filteredOption.id, spec.name)}</span>
              </button>
            ))}
          </div>
        )}

        {error && (
          <p className="rounded-sm border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        {/* İki kadro iki banttır: TLL ve Akademi ayrı listelerdir, bir listenin
            iki başlığı değil. Band başlığı sayıyı da taşır, satırdaki takım
            rozeti bu yüzden kalktı. */}
        {tll.length > 0 && (
          <Panel
            title={T.members.tllSection.replace('{n}', String(tll.length))}
            bodyClassName="p-2 md:p-3"
            // Bant yığınlama bağlamı açar (backdrop-blur); açık ayrıntı alttaki
            // bandın üstüne binebilsin diye bandın kendisi öne alınır.
            className={tll.some(m => m.id === openId) ? 'relative z-20' : ''}
          >
            {renderRoster(tll)}
          </Panel>
        )}

        {tlla.length > 0 && (
          <Panel
            title={T.members.tllaSection.replace('{n}', String(tlla.length))}
            bodyClassName="p-2 md:p-3"
            className={tlla.some(m => m.id === openId) ? 'relative z-20' : ''}
          >
            {renderRoster(tlla)}
          </Panel>
        )}

        {!loading && members.length > 0 && visible.length === 0 && (
          <PanelEmpty>{T.members.noMatches}</PanelEmpty>
        )}

        {canEdit && <WdlRosterPanel onChanged={refreshQuiet} />}

        {canEdit && (
          <MemberManageDialog
            member={managing}
            onClose={() => setManagingId(null)}
            onSteamChanged={(id, change) => patchMember(id, change)}
            onNotesChanged={(id, count) => patchMember(id, { note_count: count })}
          />
        )}

        {!loading && members.length === 0 && !error && (
          <PanelEmpty>{T.members.noMembers}</PanelEmpty>
        )}
      </div>
    </div>
  )
}
